import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import classNames from 'classnames';
import isArray from 'lodash-es/isArray';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Icon from '@panneau/element-icon';
import Link from '@panneau/element-link';

var propTypes = {
  page: PropTypes.number,
  lastPage: PropTypes.number,
  total: PropTypes.number,
  url: PropTypes.string,
  query: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  maxPages: PropTypes.number,
  loading: PropTypes.bool,
  withPreviousNext: PropTypes.bool,
  withCount: PropTypes.bool,
  autohide: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right']),
  previousLabel: PropTypes.node,
  nextLabel: PropTypes.node,
  countLabel: PropTypes.node,
  alwaysShowButtons: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectionChange: PropTypes.func,
  multipleSelection: PropTypes.bool,
  className: PropTypes.string,
  paginationClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  linkClassName: PropTypes.string,
  onClickPage: PropTypes.func
};
var defaultProps = {
  page: 1,
  lastPage: 1,
  total: null,
  url: null,
  query: null,
  maxPages: 8,
  loading: false,
  withPreviousNext: false,
  withCount: true,
  autohide: false,
  align: 'right',
  previousLabel: /*#__PURE__*/React.createElement(Icon, {
    name: "arrow-left-short"
  }),
  nextLabel: /*#__PURE__*/React.createElement(Icon, {
    name: "arrow-right-short"
  }),
  countLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "4sViWl",
    defaultMessage: [{
      "type": 6,
      "pluralType": "cardinal",
      "value": "count",
      "offset": 0,
      "options": {
        "=0": {
          "value": [{
            "type": 0,
            "value": "No item."
          }]
        },
        "=1": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " item"
          }]
        },
        "other": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " items"
          }]
        }
      }
    }]
  }),
  alwaysShowButtons: false,
  selectable: false,
  selectedItems: null,
  onSelectionChange: null,
  multipleSelection: false,
  className: null,
  paginationClassName: null,
  itemClassName: null,
  linkClassName: null,
  onClickPage: null
};
var Pagination = function Pagination(_ref) {
  var parentPage = _ref.page,
    parentLastPage = _ref.lastPage,
    parentTotal = _ref.total,
    url = _ref.url,
    query = _ref.query,
    parentMaxPages = _ref.maxPages,
    loading = _ref.loading,
    withPreviousNext = _ref.withPreviousNext,
    withCount = _ref.withCount,
    autohide = _ref.autohide,
    align = _ref.align,
    previousLabel = _ref.previousLabel,
    nextLabel = _ref.nextLabel,
    countLabel = _ref.countLabel,
    alwaysShowButtons = _ref.alwaysShowButtons,
    selectable = _ref.selectable,
    selectedItems = _ref.selectedItems,
    onSelectionChange = _ref.onSelectionChange;
    _ref.multipleSelection;
    var className = _ref.className,
    paginationClassName = _ref.paginationClassName,
    itemClassName = _ref.itemClassName,
    linkClassName = _ref.linkClassName,
    onClickPage = _ref.onClickPage;
  var page = parseInt(parentPage, 10);
  var lastPage = parseInt(parentLastPage, 10);
  var total = parseInt(parentTotal, 10);
  var maxPages = parseInt(parentMaxPages, 10);
  var getUrl = useCallback(function (currentPage) {
    return url !== null ? "".concat(url, "?").concat(queryString.stringify(_objectSpread(_objectSpread({}, query), {}, {
      page: currentPage
    }), {
      arrayFormat: 'bracket'
    })) : null;
  }, [url, query]);
  var pageNumbers = Array.from({
    length: parseInt(lastPage, 10)
  }, function (_, i) {
    return i + 1;
  });
  var stripPages = maxPages !== null && lastPage > maxPages;
  var startPage = stripPages ? Math.min(Math.max(page - maxPages / 2, 1), lastPage - maxPages) : null;
  var endPage = stripPages ? startPage + maxPages : null;
  var strippedPages = stripPages ? pageNumbers.reduce(function (selectedPages, pageNumber) {
    if (pageNumber === 1 && startPage - 1 > 1) {
      return [pageNumber, '...'];
    }
    if (pageNumber === lastPage && endPage + 1 < lastPage) {
      return [].concat(_toConsumableArray(selectedPages), ['...', pageNumber]);
    }
    return pageNumber >= startPage && pageNumber <= endPage ? [].concat(_toConsumableArray(selectedPages), [pageNumber]) : selectedPages;
  }, []) : pageNumbers;
  var pages = strippedPages.length > 0 ? strippedPages : [1];
  var element = /*#__PURE__*/React.cloneElement(countLabel, {
    values: {
      count: total
    }
  });
  var selectedCount = useMemo(function () {
    if (isArray(selectedItems)) {
      return selectedItems.length;
    }
    return selectedItems !== null ? 1 : 0;
  }, [selectedItems]);
  var onClearSelection = useCallback(function () {
    onSelectionChange(null);
  }, [onSelectionChange]);
  if (autohide && lastPage < 2) {
    return null;
  }
  var count = /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-center"
  }, selectable && selectedCount > 0 ? /*#__PURE__*/React.createElement("small", {
    className: "text-small text-nowrap text-muted fw-normal"
  }, /*#__PURE__*/React.createElement("span", {
    className: "d-inline-block"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "lsBBVh",
    defaultMessage: [{
      "type": 6,
      "pluralType": "cardinal",
      "value": "count",
      "offset": 0,
      "options": {
        "=0": {
          "value": [{
            "type": 0,
            "value": "no items"
          }]
        },
        "one": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " item"
          }]
        },
        "other": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " items"
          }]
        }
      }
    }, {
      "type": 0,
      "value": " selected"
    }],
    values: {
      count: selectedCount
    }
  }))) : null, selectable && onSelectionChange !== null && selectedCount > 0 ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn badge rounded-pill text-bg-secondary mx-2",
    onClick: onClearSelection
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "NeSNvW",
    defaultMessage: [{
      "type": 0,
      "value": "clear"
    }]
  }), /*#__PURE__*/React.createElement(Icon, {
    className: "ps-1",
    name: "x",
    bold: true
  })) : null, /*#__PURE__*/React.createElement("span", {
    className: "text-muted"
  }, element));
  return /*#__PURE__*/React.createElement("nav", {
    className: classNames(['d-flex', 'align-items-center', 'm-0', _defineProperty({
      'justify-content-end': align === 'right'
    }, className, className !== null)])
  }, loading && align === 'right' ? /*#__PURE__*/React.createElement("div", {
    className: "spinner-border text-secondary spinner-border-sm mx-1",
    role: "status"
  }, /*#__PURE__*/React.createElement("span", {
    className: "visually-hidden"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "0xeItT",
    defaultMessage: [{
      "type": 0,
      "value": "Loading..."
    }]
  }))) : null, total !== null && total > 0 && withCount && align === 'right' ? /*#__PURE__*/React.createElement("div", {
    className: "mx-3"
  }, count) : null, /*#__PURE__*/React.createElement("ul", {
    className: classNames(['pagination', 'm-0', _defineProperty({}, paginationClassName, paginationClassName !== null)])
  }, withPreviousNext && (pages.length > 1 || alwaysShowButtons) ? /*#__PURE__*/React.createElement("li", {
    className: classNames(['page-item', // 'rounded',
    _defineProperty({
      disabled: page <= 1
    }, itemClassName, itemClassName !== null)])
  }, page > 1 ? /*#__PURE__*/React.createElement(Link, {
    className: classNames(['page-link', 'rounded-0', 'rounded-start', _defineProperty({}, linkClassName, linkClassName !== null)]),
    href: getUrl(page - 1),
    onClick: onClickPage !== null ? function (e) {
      return onClickPage(page - 1, e);
    } : null
  }, previousLabel) : /*#__PURE__*/React.createElement("span", {
    className: classNames(['page-link', 'rounded-0', 'rounded-start', _defineProperty({}, linkClassName, linkClassName !== null)])
  }, previousLabel)) : null, pages.length > 1 ? pages.map(function (pageNumber, index) {
    return /*#__PURE__*/React.createElement("li", {
      key: "page-".concat(pageNumber, "-").concat(index),
      className: classNames(['page-item', _defineProperty({
        disabled: pageNumber === '...' || pages.length < 2,
        active: pageNumber === page && pages.length > 1
      }, itemClassName, itemClassName !== null)])
    }, /*#__PURE__*/React.createElement(Link, {
      className: classNames(['page-link', 'rounded-0', _defineProperty({}, linkClassName, linkClassName !== null)]),
      href: pageNumber !== '...' ? getUrl(pageNumber) : '#',
      onClick: pageNumber !== '...' && onClickPage !== null ? function (e) {
        return onClickPage(pageNumber, e);
      } : null,
      style: {
        zIndex: 0
      }
    }, pageNumber));
  }) : null, withPreviousNext && (pages.length > 1 || alwaysShowButtons) ? /*#__PURE__*/React.createElement("li", {
    className: classNames(['page-item', _defineProperty({
      disabled: page >= lastPage
    }, itemClassName, itemClassName !== null)])
  }, page < lastPage ? /*#__PURE__*/React.createElement(Link, {
    className: classNames(['page-link', 'rounded-0', 'rounded-end', _defineProperty({}, linkClassName, linkClassName !== null)]),
    href: getUrl(page + 1),
    onClick: onClickPage !== null ? function (e) {
      return onClickPage(page + 1, e);
    } : null
  }, nextLabel) : /*#__PURE__*/React.createElement("span", {
    className: classNames(['page-link', 'rounded-0', 'rounded-end', _defineProperty({}, linkClassName, linkClassName !== null)])
  }, nextLabel)) : null), total !== null && total > 0 && withCount && align === 'left' ? /*#__PURE__*/React.createElement("div", {
    className: "mx-3"
  }, count) : null, loading && align === 'left' ? /*#__PURE__*/React.createElement("div", {
    className: "spinner-border text-secondary spinner-border-sm mx-1",
    role: "status"
  }, /*#__PURE__*/React.createElement("span", {
    className: "visually-hidden"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "0xeItT",
    defaultMessage: [{
      "type": 0,
      "value": "Loading..."
    }]
  }))) : null);
};
Pagination.propTypes = propTypes;
Pagination.defaultProps = defaultProps;

export { Pagination as default };
